import type { SalesDemoAction } from "./types";

export type RestaurantDemoTableStatus = "available" | "reserved" | "occupied";
export type RestaurantDemoDerivedTableStatus = RestaurantDemoTableStatus | "unavailable";
export type RestaurantDemoReservationStatus = "confirmed" | "seated" | "completed" | "cancelled" | "no_show";
export type RestaurantDemoOrderStatus = "submitted" | "preparing" | "ready" | "completed" | "cancelled";

export interface RestaurantDemoCategory {
  readonly id: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly active: boolean;
}

export interface RestaurantDemoArea {
  readonly id: string;
  readonly name: string;
  readonly sortOrder: number;
  readonly active: boolean;
}

export interface RestaurantDemoProduct {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly description: string;
  readonly priceMinor: number;
  readonly available: boolean;
  readonly featured: boolean;
  readonly tags: readonly string[];
  readonly active: boolean;
  readonly sortOrder: number;
}

export interface RestaurantDemoTable {
  readonly id: string;
  readonly label: string;
  readonly area: string;
  readonly capacity: number;
  readonly status: RestaurantDemoTableStatus;
  readonly active: boolean;
  readonly notes: string;
}

export interface RestaurantDemoReservation {
  readonly id: string;
  readonly customerName: string;
  readonly phone: string;
  readonly partySize: number;
  readonly time: string;
  readonly date: string;
  readonly startTime: string;
  readonly durationMinutes: number;
  readonly tableId: string | null;
  readonly status: RestaurantDemoReservationStatus;
  readonly notes: string;
  readonly createdAt: string;
}

export interface RestaurantDemoOrderLine {
  readonly productId: string;
  readonly name: string;
  readonly quantity: number;
  readonly unitPriceMinor: number;
}

export interface RestaurantDemoOrder {
  readonly id: string;
  readonly customerName: string;
  readonly tableId: string | null;
  readonly channel: "pos" | "assistant" | "manual";
  readonly status: RestaurantDemoOrderStatus;
  readonly paymentStatus: "pending" | "paid";
  readonly paymentMethod: "cash" | "card" | "link" | null;
  readonly lines: readonly RestaurantDemoOrderLine[];
  readonly totalMinor: number;
  readonly createdAt: string;
}

export interface RestaurantDemoInventoryItem {
  readonly id: string;
  readonly name: string;
  readonly unit: string;
  readonly onHand: number;
  readonly minLevel: number;
  readonly active: boolean;
  readonly notes: string;
}

export interface RestaurantDemoInventoryMovement {
  readonly id: string;
  readonly inventoryItemId: string;
  readonly delta: number;
  readonly reason: string;
  readonly at: string;
}

export interface RestaurantDemoCustomer {
  readonly id: string;
  readonly name: string;
  readonly phone: string;
  readonly visits: number;
  readonly totalSpendMinor: number;
  readonly notes: string;
  readonly lastInteractionAt: string;
}

export interface RestaurantDemoEvent {
  readonly id: string;
  readonly at: string;
  readonly type: string;
  readonly label: string;
}

export interface RestaurantDemoState {
  readonly version: 2;
  readonly businessName: string;
  readonly categories: readonly RestaurantDemoCategory[];
  readonly areas: readonly RestaurantDemoArea[];
  readonly products: readonly RestaurantDemoProduct[];
  readonly tables: readonly RestaurantDemoTable[];
  readonly reservations: readonly RestaurantDemoReservation[];
  readonly orders: readonly RestaurantDemoOrder[];
  readonly inventory: readonly RestaurantDemoInventoryItem[];
  readonly inventoryMovements: readonly RestaurantDemoInventoryMovement[];
  readonly customers: readonly RestaurantDemoCustomer[];
  readonly events: readonly RestaurantDemoEvent[];
}

export interface RestaurantDemoAgentResult {
  readonly response: string;
  readonly actions: readonly SalesDemoAction[];
  readonly state: RestaurantDemoState;
}

const STORAGE_PREFIX = "clickdialog:sales-demo:restaurant:";
const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 });

function nowIso(): string {
  return new Date().toISOString();
}

function uid(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function localDateString(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addLocalDays(dateString: string, days: number): string {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year!, month! - 1, day! + days);
  return localDateString(date);
}

function normalized(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function price(minor: number): string {
  return money.format(minor / 100);
}

function storageKey(slug: string): string {
  return `${STORAGE_PREFIX}${slug}`;
}

function newEvent(type: string, label: string): RestaurantDemoEvent {
  return Object.freeze({ id: uid("evt"), at: nowIso(), type, label });
}

function addEvent(state: RestaurantDemoState, type: string, label: string): RestaurantDemoState {
  return Object.freeze({ ...state, events: Object.freeze([newEvent(type, label), ...state.events].slice(0, 60)) });
}

function category(name: string, sortOrder: number): RestaurantDemoCategory {
  return Object.freeze({ id: `cat-${normalized(name).replaceAll(" ", "-")}`, name, sortOrder, active: true });
}

function area(name: string, sortOrder: number): RestaurantDemoArea {
  return Object.freeze({ id: `area-${normalized(name).replaceAll(" ", "-")}`, name, sortOrder, active: true });
}

export function createRestaurantDemoSeed(businessName: string): RestaurantDemoState {
  const today = localDateString();
  const createdAt = nowIso();
  return Object.freeze({
    version: 2,
    businessName,
    categories: Object.freeze([category("Entradas", 10), category("Pizzas", 20), category("Especialidades", 30), category("Postres", 40)]),
    areas: Object.freeze([area("Salón", 10), area("Patio", 20)]),
    products: Object.freeze([
      Object.freeze({ id: "prod-muzza", name: "Pizza Muzzarella", category: "Pizzas", description: "Salsa de tomate, muzzarella y aceitunas.", priceMinor: 1450000, available: true, featured: true, tags: Object.freeze(["pizza", "clásica", "vegetariana"]), active: true, sortOrder: 10 }),
      Object.freeze({ id: "prod-napo", name: "Pizza Napolitana", category: "Pizzas", description: "Muzzarella, tomate fresco, ajo y orégano.", priceMinor: 1700000, available: true, featured: true, tags: Object.freeze(["pizza", "vegetariana"]), active: true, sortOrder: 20 }),
      Object.freeze({ id: "prod-fugazza", name: "Fugazzeta Rellena", category: "Pizzas", description: "Cebolla, muzzarella y doble relleno.", priceMinor: 1850000, available: true, featured: true, tags: Object.freeze(["pizza", "vegetariana", "especialidad"]), active: true, sortOrder: 30 }),
      Object.freeze({ id: "prod-empanada", name: "Empanada de carne cortada a cuchillo", category: "Entradas", description: "Carne, cebolla, huevo y especias.", priceMinor: 280000, available: true, featured: false, tags: Object.freeze(["entrada", "carne"]), active: true, sortOrder: 40 }),
      Object.freeze({ id: "prod-burrata", name: "Burrata de la casa", category: "Entradas", description: "Burrata, tomates asados, rúcula y oliva.", priceMinor: 920000, available: true, featured: true, tags: Object.freeze(["entrada", "vegetariana", "sin gluten declarado"]), active: true, sortOrder: 50 }),
      Object.freeze({ id: "prod-flan", name: "Flan casero", category: "Postres", description: "Con dulce de leche y crema.", priceMinor: 480000, available: true, featured: false, tags: Object.freeze(["postre"]), active: true, sortOrder: 60 }),
    ]),
    tables: Object.freeze([
      Object.freeze({ id: "table-1", label: "Mesa 1", area: "Salón", capacity: 2, status: "available" as const, active: true, notes: "Cerca de la ventana." }),
      Object.freeze({ id: "table-2", label: "Mesa 2", area: "Salón", capacity: 4, status: "occupied" as const, active: true, notes: "" }),
      Object.freeze({ id: "table-3", label: "Mesa 3", area: "Salón", capacity: 4, status: "available" as const, active: true, notes: "" }),
      Object.freeze({ id: "table-4", label: "Mesa 4", area: "Salón", capacity: 6, status: "reserved" as const, active: true, notes: "Ideal para grupos." }),
      Object.freeze({ id: "table-5", label: "Mesa 5", area: "Patio", capacity: 4, status: "available" as const, active: true, notes: "Exterior." }),
      Object.freeze({ id: "table-6", label: "Mesa 6", area: "Patio", capacity: 8, status: "available" as const, active: true, notes: "Mesa grande." }),
      Object.freeze({ id: "table-7", label: "Mesa 7", area: "Patio", capacity: 2, status: "occupied" as const, active: true, notes: "" }),
      Object.freeze({ id: "table-8", label: "Mesa 8", area: "Patio", capacity: 6, status: "available" as const, active: true, notes: "" }),
    ]),
    reservations: Object.freeze([
      Object.freeze({ id: "res-occupied-2", customerName: "Mesa 2", phone: "", partySize: 4, time: "20:00", date: today, startTime: "20:00", durationMinutes: 120, tableId: "table-2", status: "seated" as const, notes: "Consumo en salón.", createdAt }),
      Object.freeze({ id: "res-occupied-7", customerName: "Mesa 7", phone: "", partySize: 2, time: "20:30", date: today, startTime: "20:30", durationMinutes: 120, tableId: "table-7", status: "seated" as const, notes: "", createdAt }),
      Object.freeze({ id: "res-1", customerName: "Lucía Gómez", phone: "351 555 1001", partySize: 6, time: "21:00", date: today, startTime: "21:00", durationMinutes: 90, tableId: "table-4", status: "confirmed" as const, notes: "Prefiere salón.", createdAt }),
      Object.freeze({ id: "res-2", customerName: "Martín Ríos", phone: "351 555 1088", partySize: 4, time: "22:00", date: today, startTime: "22:00", durationMinutes: 90, tableId: "table-5", status: "confirmed" as const, notes: "Viene con niños.", createdAt }),
      Object.freeze({ id: "res-3", customerName: "Camila Suárez", phone: "351 555 2481", partySize: 2, time: "20:30", date: addLocalDays(today, 1), startTime: "20:30", durationMinutes: 90, tableId: "table-1", status: "confirmed" as const, notes: "Aniversario.", createdAt }),
    ]),
    orders: Object.freeze([
      Object.freeze({ id: "ord-101", customerName: "Mesa 2", tableId: "table-2", channel: "pos" as const, status: "preparing" as const, paymentStatus: "pending" as const, paymentMethod: null, lines: Object.freeze([Object.freeze({ productId: "prod-muzza", name: "Pizza Muzzarella", quantity: 1, unitPriceMinor: 1450000 }), Object.freeze({ productId: "prod-empanada", name: "Empanada de carne cortada a cuchillo", quantity: 2, unitPriceMinor: 280000 })]), totalMinor: 2010000, createdAt }),
      Object.freeze({ id: "ord-102", customerName: "Sofía", tableId: null, channel: "assistant" as const, status: "ready" as const, paymentStatus: "paid" as const, paymentMethod: "link" as const, lines: Object.freeze([Object.freeze({ productId: "prod-napo", name: "Pizza Napolitana", quantity: 1, unitPriceMinor: 1700000 })]), totalMinor: 1700000, createdAt }),
    ]),
    inventory: Object.freeze([
      Object.freeze({ id: "inv-muzza", name: "Muzzarella", unit: "kg", onHand: 14.8, minLevel: 8, active: true, notes: "Proveedor semanal." }),
      Object.freeze({ id: "inv-harina", name: "Harina 000", unit: "kg", onHand: 23, minLevel: 10, active: true, notes: "" }),
      Object.freeze({ id: "inv-tomate", name: "Tomate", unit: "kg", onHand: 6.2, minLevel: 5, active: true, notes: "" }),
      Object.freeze({ id: "inv-carne", name: "Carne vacuna", unit: "kg", onHand: 3.4, minLevel: 4, active: true, notes: "Reponer mañana." }),
      Object.freeze({ id: "inv-burrata", name: "Burrata", unit: "u", onHand: 7, minLevel: 6, active: true, notes: "" }),
    ]),
    inventoryMovements: Object.freeze([
      Object.freeze({ id: "mov-seed-1", inventoryItemId: "inv-carne", delta: -1.2, reason: "Producción", at: createdAt }),
      Object.freeze({ id: "mov-seed-2", inventoryItemId: "inv-muzza", delta: 5, reason: "Reposición", at: createdAt }),
    ]),
    customers: Object.freeze([
      Object.freeze({ id: "cus-1", name: "Lucía Gómez", phone: "351 555 1001", visits: 7, totalSpendMinor: 12850000, notes: "Prefiere patio.", lastInteractionAt: createdAt }),
      Object.freeze({ id: "cus-2", name: "Martín Ríos", phone: "351 555 1088", visits: 3, totalSpendMinor: 6520000, notes: "Suele venir con niños.", lastInteractionAt: createdAt }),
      Object.freeze({ id: "cus-3", name: "Sofía", phone: "351 555 7788", visits: 5, totalSpendMinor: 9340000, notes: "Compra por WhatsApp.", lastInteractionAt: createdAt }),
    ]),
    events: Object.freeze([
      Object.freeze({ id: "evt-seed-1", at: createdAt, type: "order", label: "Pedido de Sofía listo para retirar" }),
      Object.freeze({ id: "evt-seed-2", at: createdAt, type: "reservation", label: "Reserva de Lucía Gómez confirmada para las 21:00" }),
    ]),
  });
}

function uniqueNamed(values: readonly string[], kind: "cat" | "area"): readonly (RestaurantDemoCategory | RestaurantDemoArea)[] {
  return Object.freeze([...new Set(values.filter(Boolean))].map((name, index) => Object.freeze({ id: `${kind}-${normalized(name).replaceAll(" ", "-") || index}`, name, sortOrder: (index + 1) * 10, active: true })));
}

function hydrateRestaurantDemoState(value: unknown, businessName: string): RestaurantDemoState | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.products) || !Array.isArray(record.tables) || !Array.isArray(record.reservations) || !Array.isArray(record.orders) || !Array.isArray(record.inventory) || !Array.isArray(record.customers)) return null;
  const today = localDateString();
  const fallbackNow = nowIso();
  const products = (record.products as Record<string, unknown>[]).map((item, index) => Object.freeze({
    ...item,
    id: String(item.id ?? uid("prod")),
    name: String(item.name ?? "Producto"),
    category: String(item.category ?? "Otros"),
    description: String(item.description ?? ""),
    priceMinor: Number(item.priceMinor ?? 0),
    available: item.available !== false,
    featured: item.featured === true,
    tags: Object.freeze(Array.isArray(item.tags) ? item.tags.map(String) : []),
    active: item.active !== false,
    sortOrder: Number(item.sortOrder ?? (index + 1) * 10),
  })) as RestaurantDemoProduct[];
  const tables = (record.tables as Record<string, unknown>[]).map((item) => Object.freeze({
    ...item,
    id: String(item.id ?? uid("table")),
    label: String(item.label ?? "Mesa"),
    area: String(item.area ?? "Salón"),
    capacity: Math.max(1, Number(item.capacity ?? 2)),
    status: (item.status === "reserved" || item.status === "occupied" ? item.status : "available") as RestaurantDemoTableStatus,
    active: item.active !== false,
    notes: String(item.notes ?? ""),
  })) as RestaurantDemoTable[];
  const reservations = (record.reservations as Record<string, unknown>[]).map((item) => {
    const time = String(item.startTime ?? item.time ?? "21:00");
    const rawStatus = String(item.status ?? "confirmed");
    const status: RestaurantDemoReservationStatus = ["confirmed", "seated", "completed", "cancelled", "no_show"].includes(rawStatus) ? rawStatus as RestaurantDemoReservationStatus : "confirmed";
    return Object.freeze({
      ...item,
      id: String(item.id ?? uid("res")),
      customerName: String(item.customerName ?? "Cliente"),
      phone: String(item.phone ?? ""),
      partySize: Math.max(1, Number(item.partySize ?? 2)),
      time,
      date: String(item.date ?? today),
      startTime: time,
      durationMinutes: Math.max(30, Number(item.durationMinutes ?? 90)),
      tableId: item.tableId == null ? null : String(item.tableId),
      status,
      notes: String(item.notes ?? ""),
      createdAt: String(item.createdAt ?? fallbackNow),
    });
  }) as RestaurantDemoReservation[];
  const inventory = (record.inventory as Record<string, unknown>[]).map((item) => Object.freeze({
    ...item,
    id: String(item.id ?? uid("inv")),
    name: String(item.name ?? "Insumo"),
    unit: String(item.unit ?? "u"),
    onHand: Number(item.onHand ?? 0),
    minLevel: Number(item.minLevel ?? 0),
    active: item.active !== false,
    notes: String(item.notes ?? ""),
  })) as RestaurantDemoInventoryItem[];
  const customers = (record.customers as Record<string, unknown>[]).map((item) => Object.freeze({
    ...item,
    id: String(item.id ?? uid("cus")),
    name: String(item.name ?? "Cliente"),
    phone: String(item.phone ?? ""),
    visits: Number(item.visits ?? 0),
    totalSpendMinor: Number(item.totalSpendMinor ?? 0),
    notes: String(item.notes ?? ""),
    lastInteractionAt: String(item.lastInteractionAt ?? fallbackNow),
  })) as RestaurantDemoCustomer[];
  const categoryValues = Array.isArray(record.categories)
    ? (record.categories as Record<string, unknown>[]).map((item, index) => Object.freeze({ id: String(item.id ?? uid("cat")), name: String(item.name ?? "Categoría"), sortOrder: Number(item.sortOrder ?? (index + 1) * 10), active: item.active !== false }))
    : uniqueNamed(products.map((item) => item.category), "cat");
  const areaValues = Array.isArray(record.areas)
    ? (record.areas as Record<string, unknown>[]).map((item, index) => Object.freeze({ id: String(item.id ?? uid("area")), name: String(item.name ?? "Área"), sortOrder: Number(item.sortOrder ?? (index + 1) * 10), active: item.active !== false }))
    : uniqueNamed(tables.map((item) => item.area), "area");
  return Object.freeze({
    version: 2,
    businessName,
    categories: Object.freeze(categoryValues as RestaurantDemoCategory[]),
    areas: Object.freeze(areaValues as RestaurantDemoArea[]),
    products: Object.freeze(products),
    tables: Object.freeze(tables),
    reservations: Object.freeze(reservations),
    orders: Object.freeze(record.orders as RestaurantDemoOrder[]),
    inventory: Object.freeze(inventory),
    inventoryMovements: Object.freeze(Array.isArray(record.inventoryMovements) ? record.inventoryMovements as RestaurantDemoInventoryMovement[] : []),
    customers: Object.freeze(customers),
    events: Object.freeze(Array.isArray(record.events) ? record.events as RestaurantDemoEvent[] : []),
  });
}

export function loadRestaurantDemoState(slug: string, businessName: string): RestaurantDemoState {
  if (typeof window === "undefined") return createRestaurantDemoSeed(businessName);
  try {
    const raw = window.localStorage.getItem(storageKey(slug));
    if (!raw) return createRestaurantDemoSeed(businessName);
    const parsed: unknown = JSON.parse(raw);
    return hydrateRestaurantDemoState(parsed, businessName) ?? createRestaurantDemoSeed(businessName);
  } catch {
    return createRestaurantDemoSeed(businessName);
  }
}

export function saveRestaurantDemoState(slug: string, state: RestaurantDemoState): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(storageKey(slug), JSON.stringify(state));
}

export function resetRestaurantDemoState(slug: string, businessName: string): RestaurantDemoState {
  const seed = createRestaurantDemoSeed(businessName);
  saveRestaurantDemoState(slug, seed);
  return seed;
}

function minutesFromTime(value: string): number {
  const [hour, minute] = value.split(":").map(Number);
  return (Number.isFinite(hour) ? hour! : 0) * 60 + (Number.isFinite(minute) ? minute! : 0);
}

export function timeFromMinutes(total: number): string {
  const normalizedMinutes = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalizedMinutes / 60)).padStart(2, "0")}:${String(normalizedMinutes % 60).padStart(2, "0")}`;
}

export function timeRangesOverlap(startA: string, durationA: number, startB: string, durationB: number): boolean {
  const a0 = minutesFromTime(startA);
  const a1 = a0 + durationA;
  const b0 = minutesFromTime(startB);
  const b1 = b0 + durationB;
  return a0 < b1 && b0 < a1;
}

export function getTableAvailability(
  state: RestaurantDemoState,
  tableId: string,
  date: string,
  time: string,
  durationMinutes = 90,
  excludeReservationId?: string | null,
): RestaurantDemoDerivedTableStatus {
  const table = state.tables.find((candidate) => candidate.id === tableId);
  if (!table || !table.active) return "unavailable";
  const conflicts = state.reservations.filter((reservation) =>
    reservation.id !== excludeReservationId
    && reservation.tableId === tableId
    && reservation.date === date
    && (reservation.status === "confirmed" || reservation.status === "seated")
    && timeRangesOverlap(time, durationMinutes, reservation.startTime, reservation.durationMinutes));
  if (conflicts.some((reservation) => reservation.status === "seated")) return "occupied";
  if (conflicts.length > 0) return "reserved";
  return "available";
}

export function findAvailableTables(
  state: RestaurantDemoState,
  date: string,
  time: string,
  durationMinutes: number,
  partySize: number,
  areaName?: string | null,
  excludeReservationId?: string | null,
): readonly RestaurantDemoTable[] {
  return Object.freeze([...state.tables]
    .filter((table) => table.active && table.capacity >= partySize && (!areaName || table.area === areaName))
    .filter((table) => getTableAvailability(state, table.id, date, time, durationMinutes, excludeReservationId) === "available")
    .sort((a, b) => a.capacity - b.capacity || a.label.localeCompare(b.label, "es")));
}

export function reservationConflicts(state: RestaurantDemoState, reservation: RestaurantDemoReservation): readonly RestaurantDemoReservation[] {
  if (!reservation.tableId || reservation.status === "cancelled" || reservation.status === "no_show") return Object.freeze([]);
  return Object.freeze(state.reservations.filter((candidate) =>
    candidate.id !== reservation.id
    && candidate.tableId === reservation.tableId
    && candidate.date === reservation.date
    && (candidate.status === "confirmed" || candidate.status === "seated")
    && timeRangesOverlap(reservation.startTime, reservation.durationMinutes, candidate.startTime, candidate.durationMinutes)));
}

function productMentions(state: RestaurantDemoState, input: string): readonly RestaurantDemoProduct[] {
  const text = normalized(input);
  const textTokens = new Set(text.split(" ").filter((token) => token.length >= 3));
  return state.products.filter((product) => {
    if (!product.active) return false;
    const name = normalized(product.name);
    if (text.includes(name)) return true;
    const productTokens = name.split(" ").filter((token) => token.length >= 4 && !["pizza", "casa", "grande"].includes(token));
    return productTokens.some((token) => textTokens.has(token));
  });
}

function inventoryMentions(state: RestaurantDemoState, input: string): readonly RestaurantDemoInventoryItem[] {
  const text = normalized(input);
  return state.inventory.filter((item) => item.active && (text.includes(normalized(item.name)) || normalized(item.name).split(" ").some((token) => token.length >= 4 && text.includes(token))));
}

function partySizeFrom(input: string): number | null {
  const match = input.match(/(?:somos|para|mesa\s+para)\s+(\d{1,2})|(?:\b(\d{1,2})\s+personas?\b)/i);
  const digits = match?.[1] ?? match?.[2];
  const value = digits ? Number(digits) : NaN;
  return Number.isInteger(value) && value > 0 && value <= 30 ? value : null;
}

function timeFrom(input: string): string | null {
  const explicit = input.match(/(?:a\s+las|las)\s+(\d{1,2})(?::(\d{2}))?|\b(\d{1,2}):(\d{2})\b/i);
  if (!explicit) return null;
  const hour = Number(explicit[1] ?? explicit[3]);
  const minute = Number(explicit[2] ?? explicit[4] ?? "00");
  if (!Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function dateFrom(input: string): string {
  const today = localDateString();
  const text = normalized(input);
  if (text.includes("manana")) return addLocalDays(today, 1);
  const explicit = input.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  return explicit?.[0] ?? today;
}

function customerNameFrom(input: string): string | null {
  const match = input.match(/(?:a\s+nombre\s+de|soy|me\s+llamo)\s+([a-záéíóúñü]+(?:\s+[a-záéíóúñü]+)?)/i);
  return match?.[1]?.trim() ?? null;
}

function priceArsFrom(input: string): number | null {
  const match = input.match(/(?:precio\s+(?:a\s+)?|pon(?:e|é|eme)?\s+(?:a\s+)?|cambi(?:a|á)(?:\s+el\s+precio)?\s+(?:a\s+)?|a\s+\$?)\s*\$?\s*([0-9][0-9.]*)/i);
  if (!match?.[1]) return null;
  const value = Number(match[1].replaceAll(".", ""));
  return Number.isSafeInteger(value) && value >= 0 && value <= 10_000_000 ? value : null;
}

function upsertCustomer(state: RestaurantDemoState, name: string, phone = ""): RestaurantDemoState {
  const existing = state.customers.find((customer) => normalized(customer.name) === normalized(name));
  if (existing) return Object.freeze({ ...state, customers: Object.freeze(state.customers.map((customer) => customer.id === existing.id ? Object.freeze({ ...customer, phone: phone || customer.phone, lastInteractionAt: nowIso() }) : customer)) });
  return Object.freeze({ ...state, customers: Object.freeze([...state.customers, Object.freeze({ id: uid("cus"), name, phone, visits: 0, totalSpendMinor: 0, notes: "Creado desde el asistente demo.", lastInteractionAt: nowIso() })]) });
}

function agentResult(state: RestaurantDemoState, response: string, actions: readonly SalesDemoAction[]): RestaurantDemoAgentResult {
  return Object.freeze({ state, response, actions: Object.freeze([...actions]) });
}

export function runRestaurantDemoAgent(state: RestaurantDemoState, input: string): RestaurantDemoAgentResult {
  const text = normalized(input);
  const mentions = productMentions(state, input);
  const actions: SalesDemoAction[] = [];
  let next = state;

  if (mentions.length > 0 && /(precio|pone|poneme|poné|cambia|cambiá)/i.test(input)) {
    const candidatePriceArs = priceArsFrom(input);
    if (candidatePriceArs != null) {
      const target = mentions[0]!;
      const updated = Object.freeze({ ...target, priceMinor: candidatePriceArs * 100 });
      next = Object.freeze({ ...state, products: Object.freeze(state.products.map((product) => product.id === target.id ? updated : product)) });
      next = addEvent(next, "catalog", `${target.name}: precio actualizado a ${price(updated.priceMinor)} por el agente`);
      actions.push({ capability: "catalog.updatePrice", label: "Actualizó el precio", detail: `${target.name} quedó en ${price(updated.priceMinor)} dentro de la misma carta editable.` });
      return agentResult(next, `Listo. Cambié ${target.name} a ${price(updated.priceMinor)}. Ya lo vas a ver actualizado en Administración → Carta.`, actions);
    }
  }

  if (mentions.length > 0 && /(no disponible|pausa|pausar|sin stock|sacalo|sácalo)/i.test(input)) {
    const target = mentions[0]!;
    const updated = Object.freeze({ ...target, available: false });
    next = addEvent(Object.freeze({ ...state, products: Object.freeze(state.products.map((product) => product.id === target.id ? updated : product)) }), "catalog", `${target.name}: pausado por el agente`);
    actions.push({ capability: "catalog.availability.update", label: "Pausó el producto", detail: `${target.name} dejó de estar disponible para nuevas ventas.` });
    return agentResult(next, `Hecho. ${target.name} quedó marcado como no disponible. El cambio ya aparece en Administración → Carta.`, actions);
  }

  if (mentions.length > 0 && /(activar|activa|activá|disponible de nuevo|volver a vender)/i.test(input)) {
    const target = mentions[0]!;
    const updated = Object.freeze({ ...target, available: true });
    next = addEvent(Object.freeze({ ...state, products: Object.freeze(state.products.map((product) => product.id === target.id ? updated : product)) }), "catalog", `${target.name}: reactivado por el agente`);
    actions.push({ capability: "catalog.availability.update", label: "Reactivó el producto", detail: `${target.name} volvió a estar disponible en la carta.` });
    return agentResult(next, `Listo. ${target.name} vuelve a figurar disponible en la carta.`, actions);
  }

  if (/stock|queda|quedan|insumo|inventario/.test(text)) {
    const inventory = inventoryMentions(state, input);
    const rows = inventory.length > 0 ? inventory : state.inventory.filter((item) => item.active && item.onHand <= item.minLevel).slice(0, 4);
    actions.push({ capability: "inventory.availability.read", label: "Consultó stock", detail: "Leyó el inventario editable del mismo sandbox del restaurante." });
    if (rows.length === 0) return agentResult(state, "No encuentro un insumo concreto en tu consulta. Podés preguntarme, por ejemplo, cuánto queda de muzzarella.", actions);
    return agentResult(state, rows.map((item) => `${item.name}: ${item.onHand} ${item.unit}${item.onHand <= item.minLevel ? " · bajo mínimo" : ""}`).join("\n"), actions);
  }

  if (/mesa|reserv|lugar|disponib/.test(text)) {
    const partySize = partySizeFrom(input) ?? 2;
    const requestedTime = timeFrom(input) ?? "21:00";
    const requestedDate = dateFrom(input);
    const tables = findAvailableTables(state, requestedDate, requestedTime, 90, partySize);
    const table = tables[0] ?? null;
    actions.push({ capability: "seating.availability.read", label: "Consultó disponibilidad real", detail: `Buscó mesas para ${partySize} personas el ${requestedDate} a las ${requestedTime}, respetando reservas solapadas.` });
    if (!table) return agentResult(state, `No encuentro una mesa libre para ${partySize} personas el ${requestedDate} a las ${requestedTime}. Puedo buscar otra franja o dejar el caso al equipo.`, actions);

    const shouldCreate = /reservame|reserváme|reservar|confirm|anota|anotá|dejame|déjame/.test(text);
    if (!shouldCreate) return agentResult(state, `Sí. Para ${partySize} personas tengo disponible ${table.label} en ${table.area} el ${requestedDate} a las ${requestedTime}. Si me pasás el nombre puedo dejar la reserva registrada.`, actions);

    const name = customerNameFrom(input) ?? "Cliente demo";
    const reservation: RestaurantDemoReservation = Object.freeze({ id: uid("res"), customerName: name, phone: "", partySize, time: requestedTime, date: requestedDate, startTime: requestedTime, durationMinutes: 90, tableId: table.id, status: "confirmed", notes: "Creada por el asistente demo.", createdAt: nowIso() });
    next = Object.freeze({
      ...state,
      reservations: Object.freeze([reservation, ...state.reservations]),
      tables: Object.freeze(state.tables.map((candidate) => candidate.id === table.id ? Object.freeze({ ...candidate, status: "reserved" as const }) : candidate)),
    });
    next = upsertCustomer(next, name);
    next = addEvent(next, "reservation", `Reserva creada por el agente: ${name}, ${partySize} personas, ${requestedDate} ${requestedTime}`);
    actions.push(
      { capability: "reservation.create", label: "Creó la reserva", detail: `${table.label} quedó reservada para ${name} el ${requestedDate} a las ${requestedTime}.` },
      { capability: "crm.upsert", label: "Actualizó clientes", detail: "El contacto quedó disponible también en la sección Clientes." },
    );
    return agentResult(next, `Listo. Reservé ${table.label} para ${partySize} personas el ${requestedDate} a las ${requestedTime}, a nombre de ${name}. Ya quedó visible en Administración → Reservas.`, actions);
  }

  if (/(estado.*pedido|pedido.*estado|ordenes|órdenes)/.test(text) && mentions.length === 0) {
    const active = state.orders.filter((order) => order.status !== "completed" && order.status !== "cancelled").slice(0, 5);
    actions.push({ capability: "orders.read", label: "Consultó pedidos activos", detail: "Leyó el mismo tablero que usa Administración → Pedidos." });
    return agentResult(state, active.length === 0 ? "No hay pedidos activos ahora." : active.map((order) => `${order.id}: ${order.status} · ${price(order.totalMinor)}`).join("\n"), actions);
  }

  if (/pedido|pedir|quiero|comprar|orden/.test(text) && mentions.length > 0) {
    const sellable = mentions.filter((product) => product.active && product.available);
    actions.push({ capability: "catalog.search", label: "Consultó la carta", detail: "Identificó los productos mencionados contra la carta editable del negocio." });
    if (sellable.length === 0) return agentResult(state, `Encontré ${mentions.map((product) => product.name).join(", ")}, pero ahora figura como no disponible en la carta.`, actions);
    const totalMinor = sellable.reduce((sum, product) => sum + product.priceMinor, 0);
    const shouldCreate = /confirm|haceme|hacé|hace el pedido|tomame|tomá|quiero|comprar/.test(text);
    if (!shouldCreate) return agentResult(state, `${sellable.map((product) => `${product.name} (${price(product.priceMinor)})`).join(" + ")}. Total: ${price(totalMinor)}. Si querés, puedo dejar el pedido creado.`, actions);

    const order: RestaurantDemoOrder = Object.freeze({
      id: uid("ord"), customerName: customerNameFrom(input) ?? "Cliente asistente", tableId: null, channel: "assistant", status: "submitted", paymentStatus: "pending", paymentMethod: null,
      lines: Object.freeze(sellable.map((product) => Object.freeze({ productId: product.id, name: product.name, quantity: 1, unitPriceMinor: product.priceMinor }))), totalMinor, createdAt: nowIso(),
    });
    next = addEvent(Object.freeze({ ...state, orders: Object.freeze([order, ...state.orders]) }), "order", `Pedido creado por el agente por ${price(totalMinor)}`);
    actions.push({ capability: "order.create", label: "Creó el pedido", detail: `Generó un pedido por ${price(totalMinor)} y lo dejó visible en Pedidos.` });
    return agentResult(next, `Perfecto. Dejé creado el pedido por ${price(totalMinor)}. Ya aparece en Administración → Pedidos.`, actions);
  }

  if (mentions.length > 0) {
    const product = mentions[0]!;
    actions.push({ capability: "catalog.search", label: "Consultó la carta", detail: `Leyó el registro actual de ${product.name}, incluido precio y disponibilidad.` });
    const availability = product.active && product.available ? "Está disponible" : "Ahora figura como no disponible";
    return agentResult(state, `${availability}. ${product.name} cuesta ${price(product.priceMinor)}. ${product.description}`, actions);
  }

  if (/carta|menu|menú|que tienen|qué tienen|recomend|comer/.test(text)) {
    const products = state.products.filter((product) => product.active && product.available).sort((a, b) => Number(b.featured) - Number(a.featured) || a.sortOrder - b.sortOrder).slice(0, 6);
    actions.push({ capability: "catalog.search", label: "Consultó la carta disponible", detail: "Leyó productos, precios y disponibilidad desde Administración → Carta." });
    return agentResult(state, `Hoy te puedo ofrecer:\n${products.map((product) => `• ${product.name} — ${price(product.priceMinor)}`).join("\n")}\n\nSi me decís qué te gusta, te recomiendo algo más específico.`, actions);
  }

  if (/como esta|cómo está|resumen|hoy|negocio/.test(text)) {
    const today = localDateString();
    const availableTables = state.tables.filter((table) => getTableAvailability(state, table.id, today, "21:00", 90) === "available").length;
    const activeOrders = state.orders.filter((order) => order.status !== "completed" && order.status !== "cancelled").length;
    const lowStock = state.inventory.filter((item) => item.active && item.onHand <= item.minLevel).length;
    actions.push({ capability: "management.attention.read", label: "Compuso un resumen operativo", detail: "Combinó mesas, pedidos, reservas y stock del sandbox." });
    return agentResult(state, `A las 21:00 veo ${activeOrders} pedidos activos, ${availableTables} mesas disponibles, ${state.reservations.filter((reservation) => reservation.date === today && reservation.status === "confirmed").length} reservas confirmadas y ${lowStock} insumos en nivel bajo.`, actions);
  }

  actions.push({ capability: "conversation.context.read", label: "Leyó el contexto del negocio", detail: "El asistente tiene acceso al mismo estado navegable que la administración demo." });
  return agentResult(state, `Puedo consultar y trabajar con la carta, precios, productos que agregues, mesas por fecha y hora, reservas, pedidos y stock de ${state.businessName}. También podés pedirme que cambie el precio o pause un producto de la carta.`, actions);
}
